import type { RetainerAgreementWithFileDetails } from '@common/types/retainerAgreements';
import type { UserEntry } from '@common/types/users';
import { getMatterTypeLabel, getPracticeAreaLabel } from '@common/util';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import { Alert, Button, IconButton, Stack, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import AlertDialog from '@/components/AlertDialog';
import DownloadButton from '@/components/Buttons/DownloadButton';
import DataTable, { type Column } from '@/components/DataTable';
import { useRetainerAgreements } from '@/queries/legalDocuments';
import axiosInstance from '@/services/axiosInstance';
import { getFileDetails } from '@/services/FileService';
import { deleteRetainerAgreement } from '@/services/LegalDocumentService';
import { errorSnackbar, successSnackbar } from '@/utils/snackbar';
import { RETAINER_CHANGE_NOTE, ALL_USERS_LABEL } from './constants';
import CreateRetainerModal from './CreateRetainerModal';
import EditRetainerModal from './EditRetainerModal';
import ViewRetainerModal from './ViewRetainerModal';

interface Props {
    lawFirmId: number;
    users: UserEntry[];
    isSuperAdmin: boolean;
}

export default function RetainerAgreement({ lawFirmId, users, isSuperAdmin }: Props) {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [documentButtonState, setDocumentButtonState] = useState<
        Record<string, 'default' | 'loading' | 'success'>
    >({});

    const [selectedDocument, setSelectedDocument] =
        useState<RetainerAgreementWithFileDetails | null>(null);

    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    const queryClient = useQueryClient();

    const { data: retainerAgreements = [], isLoading } = useRetainerAgreements(lawFirmId);

    const deleteMutation = useMutation({
        mutationFn: (id: number) => deleteRetainerAgreement(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['retainerAgreements'] });
            successSnackbar('Document deleted successfully!');
        },
        onError: (error) => {
            console.error(error);
            errorSnackbar('An error occurred while deleting the document');
        },
        onSettled: () => {
            setIsDeleteModalOpen(false);
        },
    });

    const handleConfirmDelete = () => {
        if (!selectedDocument) return;
        deleteMutation.mutate(selectedDocument.retainerAgreementId);
    };

    const handleDownload = async (fileId: string, retainerAgreementId: number) => {
        if (!fileId) return;
        try {
            setDocumentButtonState((prev) => ({
                ...prev,
                [retainerAgreementId]: 'loading',
            }));
            const fileDetails = await getFileDetails(fileId);
            const fileName = fileDetails?.data?.fileName ?? 'document.pdf';

            const response = await axiosInstance.get(`api/v1/files/${fileId}`, {
                responseType: 'blob',
            });
            const url = URL.createObjectURL(new Blob([response.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            setDocumentButtonState((prev) => ({
                ...prev,
                [retainerAgreementId]: 'success',
            }));
        } catch (err) {
            console.error('Error downloading file:', err);
            errorSnackbar('An error occurred while downloading the file');
            setDocumentButtonState((prev) => ({
                ...prev,
                [retainerAgreementId]: 'default',
            }));
        }
    };

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(Number(event.target.value));
        setPage(0);
    };

    const columns: Column<RetainerAgreementWithFileDetails>[] = [
        {
            id: 'practiceArea',
            label: 'Practice Area',
            _render: (row) => getPracticeAreaLabel(row.practiceArea),
        },
        {
            id: 'matterType',
            label: 'Matter Type',
            _render: (row) => getMatterTypeLabel(row.matterType),
        },
        {
            id: 'userName',
            label: 'Available To',
            _render: (row) => (row.userId === null ? ALL_USERS_LABEL : row.userName),
        },
        {
            id: 'fileName',
            label: 'File',
        },
        {
            id: 'documentDisplayName',
            label: 'Display Name',
            _render: (row) => row.documentDisplayName || '—',
        },
        {
            id: 'fileType',
            label: 'Format',
            _render: (row) => (row.fileType === 'application/pdf' ? 'PDF' : 'DOCX'),
        },
        {
            id: 'view',
            label: 'View',
            _render: (row) => (
                <IconButton
                    style={{ height: '40px', width: '40px' }}
                    onClick={() => {
                        setSelectedDocument(row);
                        setIsViewModalOpen(true);
                    }}
                >
                    <FindInPageIcon />
                </IconButton>
            ),
        },
        {
            id: 'edit',
            label: 'Edit',
            _render: (row) => (
                <IconButton
                    style={{ height: '40px', width: '40px' }}
                    onClick={() => {
                        setSelectedDocument(row);
                        setIsEditModalOpen(true);
                    }}
                >
                    <EditOutlinedIcon />
                </IconButton>
            ),
        },
        {
            id: 'downloadLink',
            label: 'Download',
            _render: (row) => (
                <DownloadButton
                    iconState={documentButtonState[row.retainerAgreementId]}
                    onClick={() => handleDownload(row.fileId, row.retainerAgreementId)}
                />
            ),
        },
        {
            id: 'delete',
            label: 'Delete',
            _render: (row) => (
                <IconButton
                    style={{ height: '40px', width: '40px' }}
                    onClick={() => {
                        setSelectedDocument(row);
                        setIsDeleteModalOpen(true);
                    }}
                >
                    <DeleteOutlineIcon />
                </IconButton>
            ),
        },
    ];

    const columnsOfUser = columns.filter(
        (el) => el.id !== 'edit' && el.id !== 'delete' && el.id !== 'downloadLink',
    );

    return (
        <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between">
                <Typography variant="h1">Legal Documents</Typography>
                {isSuperAdmin && (
                    <Button variant="contained" onClick={() => setIsCreateModalOpen(true)}>
                        Add Document
                    </Button>
                )}
            </Stack>
            <DataTable
                generateKeyFrom="retainerAgreementId"
                isLoading={isLoading}
                columns={isSuperAdmin ? columns : columnsOfUser}
                page={page}
                rowsPerPage={rowsPerPage}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                rows={retainerAgreements}
            />
            {!isSuperAdmin && <Alert severity="info">{RETAINER_CHANGE_NOTE}</Alert>}
            <AlertDialog
                isLoading={deleteMutation.isPending}
                open={isDeleteModalOpen}
                handleClose={() => setIsDeleteModalOpen(false)}
                title="Are you sure you want to delete the retainer?"
                message="By clicking Accept you are deleting the selected retainer."
                handleConfirm={handleConfirmDelete}
            />
            {isViewModalOpen && selectedDocument && (
                <ViewRetainerModal
                    selectedDocument={selectedDocument}
                    onClose={() => {
                        setSelectedDocument(null);
                        setIsViewModalOpen(false);
                    }}
                />
            )}
            {isCreateModalOpen && (
                <CreateRetainerModal
                    users={users}
                    lawFirmId={lawFirmId}
                    onClose={() => setIsCreateModalOpen(false)}
                />
            )}
            {isEditModalOpen && selectedDocument && (
                <EditRetainerModal
                    selectedDocument={selectedDocument}
                    users={users}
                    lawFirmId={lawFirmId}
                    onClose={() => {
                        setSelectedDocument(null);
                        setIsEditModalOpen(false);
                    }}
                />
            )}
        </Stack>
    );
}
